import React, { useState, useEffect } from "react";

const debugMode = false;

const initialState = {
  q1: "",
  q2: "",
  q3: "",
  q4: "",
  q5: "",
  q6: "",
  q7: "",
  q8: "",
  q9: "",
  q10: "",
  q11: "",
  q12: "",
};

const q1Options = ["Yes", "No"];
const q2Options = ["Yes", "No"];
const q3Options = ["Yes", "No"];
const q4Options = ["Yes", "No"];
const q5Options = ["Yes", "No"];
const q6Options = ["Yes", "No"];
const q7Options = ["Yes", "No"];
const q8Options = ["Yes", "No"];
const q9Options = ["Yes", "No"];
const q10Options = ["Yes", "No"];
const q11Options = ["Yes", "No"];
const q12Options = ["Yes", "No", "I am not currently pregnant"];

const debugPointLabels = {
  q1: { Yes: "+3", No: "+0" },
  q2: { Yes: "+0", No: "+3 (shared max 3 with Q3)" },
  q3: { Yes: "+3 (shared max 3 with Q2)", No: "+0" },
  q4: { Yes: "+3", No: "+0" },
  q5: { Yes: "+2", No: "+0" },
  q6: { Yes: "+2", No: "+0" },
  q7: { Yes: "+2", No: "+0" },
  q8: { Yes: "+2 (shared max 2 with Q9)", No: "+0" },
  q9: { Yes: "+2 (shared max 2 with Q8)", No: "+0" },
  q10: { Yes: "+2", No: "+0" },
  q11: { Yes: "+0", No: "+1" },
  q12: { Yes: "+0", No: "+1", "I am not currently pregnant": "+0" },
};

export default function CARPREG2Patients() {
  const [form, setForm] = useState(initialState);
  const [showResults, setShowResults] = useState(false);
  const [urlScore, setUrlScore] = useState(null);
  const [validationError, setValidationError] = useState("");

  // Parse URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const scoreParam = urlParams.get("score");
    if (scoreParam && !isNaN(scoreParam)) {
      const parsedScore = parseInt(scoreParam);
      setUrlScore(parsedScore);
      setShowResults(true);
    }
  }, []);

  // Get risk percentage based on score
  const getEstimatedRisk = (score) => {
    if (score <= 1)
      return "Your risk of having a heart problem during pregnancy is low, with a less than 5% chance of a heart problem.";
    if (score === 2)
      return "Your risk of having a heart problem during pregnancy is in the intermediate range, with an approximately 10% chance of having a heart problem.";
    if (score === 3)
      return "Your risk of having a heart problem during pregnancy is in the intermediate range, with an approximately 15% chance of having a heart problem.";
    if (score === 4)
      return "Your risk of having a heart problem during pregnancy is in the intermediate to high range, with an approximately 22% chance of having a heart problem.";
    if (score >= 5)
      return "Your risk of having a heart problem during pregnancy is high, with an approximately 41% chance of having a heart problem.";
    return "Unknown";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Scoring logic
  const calculateScore = (form) => {
    let total = 0;

    // Q1: Yes=3, No=0
    total += form.q1 === "Yes" ? 3 : 0;

    // Q2: Yes=0, No=3
    let q2Score = form.q2 === "Yes" ? 0 : 3;

    // Q3: Yes=3, No=0
    let q3Score = form.q3 === "Yes" ? 3 : 0;

    // [Q2,Q3] max 3 points
    let q2q3 = Math.min(q2Score + q3Score, 3);
    total += q2q3;

    // Q4: Yes=3, No=0
    total += form.q4 === "Yes" ? 3 : 0;

    // Q5: Yes=2, No=0
    total += form.q5 === "Yes" ? 2 : 0;

    // Q6: Yes=2, No=0
    total += form.q6 === "Yes" ? 2 : 0;

    // Q7: Yes=2, No=0
    let q7Score = form.q7 === "Yes" ? 2 : 0;
    total += q7Score;

    // Q8: Yes=2, No=0
    let q8Score = form.q8 === "Yes" ? 2 : 0;

    // Q9: Yes=2, No=0
    let q9Score = form.q9 === "Yes" ? 2 : 0;

    // [Q8,Q9] max 2 points
    let q8q9 = Math.min(q8Score + q9Score, 2);
    total += q8q9;

    // Q10: Yes=2, No=0
    total += form.q10 === "Yes" ? 2 : 0;

    // Q11: Yes=0, No=1
    total += form.q11 === "No" ? 1 : 0;

    // Q12: Yes=0, No=1, 'I am not currently pregnant'=0
    total += form.q12 === "No" ? 1 : 0;

    return total;
  };

  const runningScore = calculateScore(form);

  const getOptionPointLabel = (questionName, option) => {
    return debugPointLabels[questionName]?.[option] ?? "+0";
  };

  const renderOptions = (questionName, options) => {
    return options.map((opt) => (
      <label key={opt} className="radio-option">
        <input
          type="radio"
          name={questionName}
          value={opt}
          checked={form[questionName] === opt}
          onChange={handleChange}
        />
        <span className="radio-option-text">{opt}</span>
        {debugMode && (
          <span className="radio-option-points">
            {getOptionPointLabel(questionName, opt)}
          </span>
        )}
      </label>
    ));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Clear any previous validation errors
    setValidationError("");

    // Validate all fields are completed
    const missingFields = [];
    for (let i = 1; i <= 12; i++) {
      if (!form[`q${i}`]) {
        missingFields.push(`Question ${i}`);
      }
    }

    if (missingFields.length > 0) {
      setValidationError(
        `Please complete all questions before submitting. Missing: ${missingFields.join(", ")}`,
      );
      return;
    }

    // Redirect to results page with score parameter
    window.location.href = `${window.location.pathname}?score=${runningScore}`;
  };

  const handleStartOver = () => {
    // Redirect to page URL without query parameters
    window.location.href = window.location.pathname;
  };

  // Show results if URL parameter is present
  if (showResults && urlScore !== null) {
    return (
      <div className="carpreg2-results">
        <div>
          <h2>CARPREG II Score: {urlScore}</h2>

          <h5>Estimated Risk</h5>
          <div className="estimated-risk">{getEstimatedRisk(urlScore)}</div>
          <p className="results-disclaimer">
            This prediction is an estimate only. Please discuss your results
            with your cardiology or obstetric care team for personalized medical
            advice.
          </p>
        </div>
        <button
          className="btn arrow-link red"
          type="button"
          onClick={handleStartOver}
        >
          <span className="title" data-title="Start Over">
            <span className="title-text">Start Over</span>
            <span className="title-bold-spacer">Start Over</span>
          </span>
          <svg
            className="link-arrow"
            width="60.9075413"
            height="23"
            viewBox="0 0 60.9075413 23"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              className="circle"
              cx="35"
              cy="11"
              r="10.5"
              stroke="#EA0029"
            />
            <line
              className="line"
              x1="0"
              y1="10.9453"
              x2="39"
              y2="10.9453"
              stroke="#EA0029"
            />
            <path
              className="arrow"
              transform-origin="100% 50%"
              stroke="#EA0029"
              d="M34,16.9452991l5-5.999999-5-5.99999"
            />
            <path
              className="heart"
              opacity="0"
              stroke="#EA0029"
              d="M58.7248557,11.1299175c.8732012-1.0419667,1.3983597-2.3794889,1.3983597-3.83914.0007368-3.322673-2.7200319-6.0156097-6.075484-6.0156097-2.0735386,0-3.904295,1.028834-5.0006808,2.5990389-1.0971225-1.5702049-2.9279353-2.5990389-5.0014175-2.5990389-3.3561325,0-6.0762207,2.6929366-6.0762207,6.0156097,0,1.4596511.5252148,2.7971733,1.398416,3.83914.1094622.1304803.2245475.2568141.3445193.3776949l9.3339662,10.2172197,9.3339662-10.2172197c.1199696-.1208808.235055-.2465321.3445757-.3776949Z"
            />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="carpreg2-form">
      {debugMode && (
        <div className="carpreg2-debug-score" aria-live="polite">
          <span className="carpreg2-debug-score-label">Running Total</span>
          <span className="carpreg2-debug-score-value">{runningScore}</span>
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <fieldset>
          <legend>
            1. Have you ever had one of the following issues:
            <ul>
              <li>Heart failure</li>
              <li>Fluid build-up in the lungs</li>
              <li>Leg swelling</li>
              <li>Needing to take water pills</li>
              <li>Stroke or transient ischemia attack</li>
              <li>Heart attack</li>
              <li>Abnormal heart rhythm requiring treatment</li>
              <li>Arrhythmias requiring treatment</li>
            </ul>
          </legend>
          {renderOptions("q1", q1Options)}
        </fieldset>

        <fieldset>
          <legend>
            2. Can you walk more than two blocks without getting short of
            breath?
          </legend>
          {renderOptions("q2", q2Options)}
        </fieldset>

        <fieldset>
          <legend>
            3. Have you ever been told that your lips or fingers turn blue
            because your blood has low oxygen, or do you have an oxygen
            saturation level below 90%?
          </legend>
          {renderOptions("q3", q3Options)}
        </fieldset>

        <fieldset>
          <legend>4. Do you have a metal heart valve?</legend>
          {renderOptions("q4", q4Options)}
        </fieldset>

        <fieldset>
          <legend>
            5. Have you been told that your heart is weaker than normal or the
            pumping function (ejection fraction) is lower than normal?
          </legend>
          {renderOptions("q5", q5Options)}
        </fieldset>

        <fieldset>
          <legend>
            6. Have you ever been told that you have a severely narrowed or
            tight aortic valve, severely narrowed or tight mitral valve,
            severely leaky mitral valve, or hypertrophic cardiomyopathy with a
            high gradient in your heart?
          </legend>
          {renderOptions("q6", q6Options)}
        </fieldset>

        <fieldset>
          <legend>
            7. Have you ever been told that the pressure in your lungs (called
            pulmonary pressure) is high?
          </legend>
          {renderOptions("q7", q7Options)}
        </fieldset>

        <fieldset>
          <legend>
            8. Have you ever had a heart attack, stents in your heart or had
            bypass surgery?
          </legend>
          {renderOptions("q8", q8Options)}
        </fieldset>

        <fieldset>
          <legend>
            9. Do you have any major blockages in the arteries of your heart?
          </legend>
          {renderOptions("q9", q9Options)}
        </fieldset>

        <fieldset>
          <legend>
            10. Have you been diagnosed with:
            <ul>
              <li>Marfan syndrome</li>
              <li>Bicuspid aortic valve with aorta measuring &gt;45 mm</li>
              <li>Loeys-Dietz syndrome</li>
              <li>Vascular Ehlers-Danlos syndrome</li>
              <li>Prior aortic dissection or tear in your aorta</li>
              <li>Aortic pseudoaneurysm</li>
            </ul>
          </legend>
          {renderOptions("q10", q10Options)}
        </fieldset>

        <fieldset>
          <legend>
            11. Have you ever had any heart surgery, stents, or other heart
            procedures?
          </legend>
          {renderOptions("q11", q11Options)}
        </fieldset>

        <fieldset>
          <legend>
            12. If you are currently pregnant, have you had an appointment with
            a Cardiologist before 20 weeks of pregnancy?
          </legend>
          {renderOptions("q12", q12Options)}
        </fieldset>

        <button className="btn arrow-link red" type="submit">
          <span className="title" data-title="Submit">
            <span className="title-text">See Your Results</span>
            <span className="title-bold-spacer">See Your Results</span>
          </span>
          <svg
            className="link-arrow"
            width="60.9075413"
            height="23"
            viewBox="0 0 60.9075413 23"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              className="circle"
              cx="35"
              cy="11"
              r="10.5"
              stroke="#EA0029"
            />
            <line
              className="line"
              x1="0"
              y1="10.9453"
              x2="39"
              y2="10.9453"
              stroke="#EA0029"
            />
            <path
              className="arrow"
              transform-origin="100% 50%"
              stroke="#EA0029"
              d="M34,16.9452991l5-5.999999-5-5.99999"
            />
            <path
              className="heart"
              opacity="0"
              stroke="#EA0029"
              d="M58.7248557,11.1299175c.8732012-1.0419667,1.3983597-2.3794889,1.3983597-3.83914.0007368-3.322673-2.7200319-6.0156097-6.075484-6.0156097-2.0735386,0-3.904295,1.028834-5.0006808,2.5990389-1.0971225-1.5702049-2.9279353-2.5990389-5.0014175-2.5990389-3.3561325,0-6.0762207,2.6929366-6.0762207,6.0156097,0,1.4596511.5252148,2.7971733,1.398416,3.83914.1094622.1304803.2245475.2568141.3445193.3776949l9.3339662,10.2172197,9.3339662-10.2172197c.1199696-.1208808.235055-.2465321.3445757-.3776949Z"
            />
          </svg>
        </button>

        {validationError && (
          <div
            style={{
              marginTop: "1em",
              padding: "10px",
              backgroundColor: "#ffe6e6",
              border: "1px solid #ff6b6b",
              borderRadius: "4px",
              color: "#d63031",
            }}
          >
            {validationError}
          </div>
        )}
      </form>
    </div>
  );
}
